import {makeEnumTypeGuard} from '../../../../libs/shared/helpers/src/typescript/enumHelpers';
import {hasProperty} from '../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {ClientAtom} from './expressionAtomModel';
import {Expression, ExpressionId} from './expressionModel';
import {ExpressionState, ExpressionValidator} from './expressionState';

/*
 * Type enum.
 */

export enum ExpressionActionTypesEnum {
  ADD_EXPRESSION = '@@expression/ADD_EXPRESSION',
  UPDATE_EXPRESSION_NAME = '@@expression/UPDATE_EXPRESSION_NAME',
  DELETE_EXPRESSION = '@@expression/DELETE_EXPRESSION',

  ADD_ATOM = '@@expression/ADD_ATOM',
  UPDATE_ATOM = '@@expression/UPDATE_ATOM',
  DELETE_ATOM = '@@expression/DELETE_ATOM',

  UPDATE_DRY_RUN = '@@expression/UPDATE_DRY_RUN',
  DRY_RUN_ERROR = '@@expression/DRY_RUN_ERROR',
  CLEAR_DRY_RUN = '@@expression/CLEAR_DRY_RUN',

  SET_PANEL_OPEN = '@@expression/SET_PANEL_STATE',
  SAVE_DRAFT_EXPRESSION = '@@expression/SAVE_DRAFT_EXPRESSION',
  SELECT_EXPRESSION = '@@expression/SELECT_EXPRESSION',
}

/*
 * Types.
 */

interface AddExpression<S extends ExpressionState> {
  type: ExpressionActionTypesEnum.ADD_EXPRESSION;
  atoms: ReadonlyArray<ClientAtom>;
}

interface UpdateExpressionName {
  type: ExpressionActionTypesEnum.UPDATE_EXPRESSION_NAME;
  expressionId: ExpressionId;
  name: string;
}

interface DeleteExpression<S extends ExpressionState> {
  type: ExpressionActionTypesEnum.DELETE_EXPRESSION;
  expressionId: ExpressionId;
  validator?: ExpressionValidator<S>;
}

interface AddAtom<S extends ExpressionState> {
  type: ExpressionActionTypesEnum.ADD_ATOM;
  expressionId: ExpressionId;
  functionAtomClientId?: string;
  atom: ClientAtom;
  validator?: ExpressionValidator<S>;
}

interface UpdateAtom<S extends ExpressionState> {
  type: ExpressionActionTypesEnum.UPDATE_ATOM;
  expressionId: ExpressionId;
  atomClientId: string;
  update(atom: ClientAtom): ClientAtom;
  validator?: ExpressionValidator<S>;
}

interface DeleteAtom<S extends ExpressionState> {
  type: ExpressionActionTypesEnum.DELETE_ATOM;
  expressionId: ExpressionId;
  atomClientId: string;
  validator?: ExpressionValidator<S>;
}

interface UpdateDryRun {
  type: ExpressionActionTypesEnum.UPDATE_DRY_RUN;
  expressions: ReadonlyArray<Expression>;
  selectedExpression: Expression | null;
}

interface DryRunError {
  type: ExpressionActionTypesEnum.DRY_RUN_ERROR;
  errorMessage: string;
}

interface ClearDryRun {
  type: ExpressionActionTypesEnum.CLEAR_DRY_RUN;
}

interface SaveDraftExpressionAction<S extends ExpressionState> {
  type: ExpressionActionTypesEnum.SAVE_DRAFT_EXPRESSION;
  validator?: ExpressionValidator<S>;
}

interface ToggleExpressionPanelAction {
  type: ExpressionActionTypesEnum.SET_PANEL_OPEN;
  isOpen: boolean;
}

interface SelectExpressionAction {
  type: ExpressionActionTypesEnum.SELECT_EXPRESSION;
  id: ExpressionId;
}

/*
 * Combined action type.
 */

export type ExpressionAction<S extends ExpressionState> =
  | AddExpression<S>
  | UpdateExpressionName
  | DeleteExpression<S>
  | AddAtom<S>
  | UpdateAtom<S>
  | DeleteAtom<S>
  | UpdateDryRun
  | ClearDryRun
  | DryRunError
  | SaveDraftExpressionAction<S>
  | ToggleExpressionPanelAction
  | SelectExpressionAction;

/*
 * Type guard.
 */

const isExpressionActionType = makeEnumTypeGuard(ExpressionActionTypesEnum);

export function isExpressionAction<S extends ExpressionState>(
  action: unknown,
): action is ExpressionAction<S> {
  return hasProperty(action, 'type') && isExpressionActionType(action.type);
}
